import { useTranslation } from "react-i18next";
import { CircleCheck, Clock, TriangleAlert } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney, formatQuantity, getStockProgress } from "../../../BidRequests/utils";
import {
  FULFILMENT_STATUS,
  type GeneratedDemand,
  type SchoolBid,
  type Settlement,
  type SettlementCommodity,
} from "../../../BidRequests/types";

interface OffersTabProps {
  bid: SchoolBid;
  demand: GeneratedDemand | null;
  settlement?: Settlement | null;
  unitCodeByUnitId?: Record<number, string>;
  showFarmland?: boolean;
}

interface AssignedStockProps {
  settlement: Settlement;
  aggregatorName: string;
  currency: SchoolBid["currency"];
  unitCodeByUnitId: Record<number, string>;
  showFarmland: boolean;
}

const CommodityStock = ({
  commodity,
  currency,
  unitCode,
  showFarmland,
}: {
  commodity: SettlementCommodity;
  currency: SchoolBid["currency"];
  unitCode?: string;
  showFarmland: boolean;
}) => {
  const { t } = useTranslation();
  const progress = getStockProgress(commodity, unitCode);
  if (!progress) return null;
  const deposits = commodity.deposits ?? [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-sm">
          {commodity.commodity_name}
          {commodity.quality_grade_name ? ` · ${commodity.quality_grade_name}` : ""}
        </p>
        <span
          className={`flex items-center gap-1.5 text-sm ${
            progress.complete ? "text-success-700" : "text-warning-700"
          }`}
        >
          {progress.complete ? (
            <CircleCheck className="size-4" />
          ) : (
            <TriangleAlert className="size-4" />
          )}
          {t("bidRequests.detail.offers.stock.assigned", {
            linked: progress.linked,
            agreed: progress.agreed,
          })}
        </span>
      </div>
      {deposits.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("bidRequests.detail.offers.stock.noDeposits")}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {showFarmland && (
                <TableHead>{t("bidRequests.detail.offers.stock.farmland")}</TableHead>
              )}
              <TableHead>{t("bidRequests.detail.offers.quantity")}</TableHead>
              <TableHead className="text-right">
                {t("bidRequests.detail.offers.unitPrice")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deposits.map(deposit => (
              <TableRow key={deposit.uuid}>
                {showFarmland && (
                  <TableCell>
                    {deposit.farmland_name ||
                      t("bidRequests.detail.offers.stock.farmlandNotProvided")}
                  </TableCell>
                )}
                <TableCell>{formatQuantity(deposit.quantity, unitCode)}</TableCell>
                <TableCell className="text-right">{formatMoney(currency, deposit.price)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const AssignedStock = ({
  settlement,
  aggregatorName,
  currency,
  unitCodeByUnitId,
  showFarmland,
}: AssignedStockProps) => {
  const { t } = useTranslation();
  const hasStockBlock = settlement.commodities.some(c => c.linked_quantity !== undefined);
  if (!hasStockBlock) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <h3 className="font-semibold">{t("bidRequests.detail.offers.stock.title")}</h3>
        <p className="text-muted-foreground text-sm">
          {t("bidRequests.detail.offers.stock.subtitle", { aggregator: aggregatorName })}
        </p>
      </div>
      {settlement.fulfilment_status === FULFILMENT_STATUS.awaitingAcceptance ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground text-sm">
          <Clock className="size-4 shrink-0" />
          {t("bidRequests.detail.offers.stock.awaitingAcceptance", {
            aggregator: aggregatorName,
          })}
        </p>
      ) : (
        settlement.commodities.map(commodity => (
          <CommodityStock
            key={commodity.child_offer_id}
            commodity={commodity}
            currency={currency}
            unitCode={
              commodity.unit_of_measure_code ?? unitCodeByUnitId[commodity.unit_of_measure_id]
            }
            showFarmland={showFarmland}
          />
        ))
      )}
    </div>
  );
};

export const OffersTab = ({
  bid,
  demand,
  settlement = null,
  unitCodeByUnitId = {},
  showFarmland = false,
}: OffersTabProps) => {
  const { t } = useTranslation();
  const currency = bid.currency;
  const hasTransport = Number(bid.transport_cost) > 0;

  return (
    <Card className="gap-0 py-0">
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-0.5">
          <h3 className="font-semibold">
            {demand
              ? t("bidRequests.detail.offers.title", { reference: demand.reference })
              : t("bidRequests.detail.offers.titleFallback")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t("bidRequests.detail.offers.sentTo", { aggregator: bid.aggregator_name })}
          </p>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("bidRequests.detail.offers.commodity")}</TableHead>
              <TableHead>{t("bidRequests.detail.offers.quantity")}</TableHead>
              <TableHead>{t("bidRequests.detail.offers.unitPrice")}</TableHead>
              <TableHead className="text-right">
                {t("bidRequests.detail.offers.lineTotal")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bid.items.map(item => (
              <TableRow key={item.id}>
                <TableCell>{item.commodity_name}</TableCell>
                <TableCell>{formatQuantity(item.quantity, item.unit_of_measure_code)}</TableCell>
                <TableCell>{formatMoney(currency, item.unit_price)}</TableCell>
                <TableCell className="text-right">
                  {formatMoney(currency, item.line_total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Separator />

        <dl className="flex flex-col gap-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("bidRequests.detail.offers.itemsTotal")}</dt>
            <dd>{formatMoney(currency, bid.items_total)}</dd>
          </div>
          {hasTransport && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t("bidRequests.detail.offers.transport")}</dt>
              <dd>{formatMoney(currency, bid.transport_cost)}</dd>
            </div>
          )}
          <div className="flex justify-between font-semibold">
            <dt>{t("bidRequests.detail.offers.total")}</dt>
            <dd>{formatMoney(currency, bid.total)}</dd>
          </div>
        </dl>

        {settlement && settlement.fulfilment_status !== FULFILMENT_STATUS.cancelled && (
          <>
            <Separator />
            <AssignedStock
              settlement={settlement}
              aggregatorName={bid.aggregator_name}
              currency={currency}
              unitCodeByUnitId={unitCodeByUnitId}
              showFarmland={showFarmland}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
